import { usePlatformStatus } from "../context/PlatformStatusContext";
import { AlertTriangle, PhoneCall, Mail, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";

const PlatformUnavailableOverlay = () => {
  const { 
    isAppEnabled, 
    isMaintenanceMode, 
    maintenanceMessage, 
    isLoading,
    checkPlatformStatus,
    isAppDisabled 
  } = usePlatformStatus();

  // Don't show anything while loading or if app is enabled
  if (isLoading || !isAppDisabled) {
    return null;
  }

  const getMessage = () => {
    if (isMaintenanceMode && maintenanceMessage) {
      return maintenanceMessage;
    }
    if (!isAppEnabled) {
      return "Fleety is currently unavailable. Please try again later or contact customer support.";
    }
    return "Fleety is currently undergoing maintenance. We'll be back soon!";
  };

  const handleRetry = async () => {
    await checkPlatformStatus();
  };

  return (
    <div 
      className="fixed inset-0 z-[9999] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4"
      data-testid="platform-unavailable-overlay"
    >
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
        {/* Icon */}
        <div className="w-20 h-20 mx-auto mb-6 bg-orange-100 rounded-full flex items-center justify-center">
          <AlertTriangle className="w-10 h-10 text-orange-500" />
        </div>

        {/* Logo */}
        <div className="mb-4">
          <h1 className="text-3xl font-bold text-[#3FA9F5]">Fleety</h1>
        </div>

        {/* Message */}
        <h2 className="text-xl font-semibold text-gray-800 mb-3">
          {isMaintenanceMode ? "Under Maintenance" : "Service Unavailable"}
        </h2>
        <p className="text-gray-600 mb-6 leading-relaxed">
          {getMessage()}
        </p>

        {/* Retry Button */}
        <Button
          onClick={handleRetry}
          className="w-full bg-[#3FA9F5] hover:bg-[#2d8fd4] text-white font-medium py-3 rounded-xl mb-4 flex items-center justify-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Try Again
        </Button>

        {/* Contact Support */}
        <div className="border-t pt-6 mt-2">
          <p className="text-sm text-gray-500 mb-4">Need immediate assistance?</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a 
              href="tel:+919845331817" 
              className="flex items-center justify-center gap-2 text-[#3FA9F5] hover:text-[#2d8fd4] text-sm font-medium"
            >
              <PhoneCall className="w-4 h-4" />
              +91-98453 31817
            </a>
            <span className="hidden sm:inline text-gray-300">|</span>
            <a 
              href="mailto:support@fleety.co.in" 
              className="flex items-center justify-center gap-2 text-[#3FA9F5] hover:text-[#2d8fd4] text-sm font-medium"
            >
              <Mail className="w-4 h-4" />
              support@fleety.co.in
            </a>
          </div>
        </div>

        {/* Status Indicator */}
        <div className="mt-6 pt-4 border-t">
          <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
            {isMaintenanceMode ? "Maintenance in progress" : "Service temporarily disabled"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformUnavailableOverlay;
